/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

// Component imports
import ReactPlayer from 'react-player';
import { Button, Col, Image, Popconfirm, Row, Space, Table, Typography } from 'antd';
import { CopyOutlined, EyeOutlined, FileImageOutlined } from '@ant-design/icons';
import MediaPreviewModal from '../Modals/MediaPreviewModal';
import { DeleteIcon } from '../Icons/DeleteIcon';

// Misc imports
import { transformFileSize } from '../../utils/misc/transformFileSize';
import { copyTextToClipboard } from '../../utils/function/copyToClipboard';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useCreateMediaFileMutation, useDeleteMediaFileMutation } from '../../store/media/mediaApi';
import { DEFAULT_DATETIME_FORMAT, DEFAULT_PAGINATION_SIZE } from '../../constants/main';

const MediaTable = ({
  page,
  selectedFiles,
  setSelectedFiles=() => {},
  handleDelete=() => {},
  urlSearchParams=() => {}
}) => {
  const { t } = useTranslation();
  const { orgId } = useParams();

  const [fileId, setFileId] = useState(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  const { data: mediaFiles, isFetching: isFetchingMediaFiles } = getCurrentQueryState('fetchMediaFiles', { orgId });
  const [, { isLoading: isDeletingMediaFile }] = useDeleteMediaFileMutation({ fixedCacheKey: 'mediaFiles-delete' });
  const [, { isLoading: isCreatingMediaFile }] = useCreateMediaFileMutation({ fixedCacheKey: 'mediaFiles-create' });

  const { data: organization } = getCurrentQueryState('fetchOrganization', { orgId });
  useEffect(() => {
    urlSearchParams(1, pageSize);
  }, []);

  const onSelectChange = (newSelectedFiles) => {
    setSelectedFiles(newSelectedFiles);
  };

  const onDropdownSelect = (key) => {
    let newSelectedFiles = [];
    if (key === 'all') {
      newSelectedFiles = mediaFiles?.data?.map(file => {
        return file.id;
      });
    }
    setSelectedFiles(newSelectedFiles);
  };

  const rowSelection = {
    selectedRowKeys: selectedFiles,
    onChange: onSelectChange,
    selections: [
      {
        key: 'all',
        text: t('All'),
        onSelect: () => onDropdownSelect('all')
      },
      {
        key: 'none',
        text: t('None'),
        onSelect: () => onDropdownSelect('none')
      }
    ]
  };

  const handleTableChange = (
    pagination,
    filters,
    sorter
  ) => {
    setPageSize(pagination.pageSize);
    urlSearchParams(
      pagination.current,
      pagination.pageSize,
      { sortBy: sorter.field, order: sorter.order === 'ascend' ? 'ASC' : 'DESC' },
      filters.fileType ? filters.fileType : null,
      filters.createdBy ? filters.createdBy : null
    );
  };

  const onClose = () => {
    setFileId(null);
    setIsPreviewModalOpen(false);
  };

  return (
    <Table
      rowSelection={rowSelection}
      rowKey="id"
      size="small"
      dataSource={mediaFiles?.data}
      loading={isFetchingMediaFiles}
      onChange={handleTableChange}
      pagination={{
        size: 'default',
        total: mediaFiles?.count,
        defaultCurrent: 1,
        current: page,
        pageSize: pageSize,
        showSizeChanger: true,
        showTotal: (total) => `${t('Total Files')}: ${total}`
      }}
      columns={[
        {
          key: 'preview',
          dataIndex: 'url',
          title: t('Preview'),
          width: 200,
          className: 'text-center',
          render: (_text, file) => {
            return (
              <Fragment>
                {
                  file.mimeType.match(/(mp4)$/) ? (
                    <Fragment>
                      <ReactPlayer
                        url={file.url}
                        style={{ maxHeight: 100, margin: 'auto', objectFit: 'cover' }}
                        onClick={() => {
                          setFileId(file.id);
                          setIsPreviewModalOpen(true);
                        }}
                        width="100%"
                      />
                    </Fragment>
                  ) : (
                    <Image
                      align="middle"
                      src={file.url}
                      style={{ width: '100%', maxHeight: 100, margin: 'auto', objectFit: 'cover' }}
                      onClick={() => {
                        setFileId(file.id);
                        setIsPreviewModalOpen(true);
                      }}
                      placeholder={
                        <Row align="middle" justify="center">
                          <FileImageOutlined />
                        </Row>
                      }
                      preview={{
                        visible: false,
                        src: file.url,
                        mask: null
                      }}
                    />
                  )
                }

                <MediaPreviewModal
                  id={fileId}
                  file={file}
                  onPreviewClose={onClose}
                  handleDelete={handleDelete}
                  isDeletingMediaFile={isDeletingMediaFile}
                  isPreviewModalOpen={isPreviewModalOpen}
                />
              </Fragment>
            );
          }
        },
        {
          title: (
            <Row wrap={false} gutter={4} align="middle">
              <Col>
                <Typography.Text ellipsis>
                  {t('File Name')}
                </Typography.Text>
              </Col>
            </Row>
          ),
          sorter: true,
          sortDirections: ['ascend', 'descend'],
          key: 'fileName',
          responsive: ['xl'],
          dataIndex: 'fileName'
        },
        {
          title: t('File Type'),
          key: 'fileType',
          filters: mediaFiles?.fileTypes ? Object.keys(mediaFiles?.fileTypes).map(key => ({ text: mediaFiles?.fileTypes[key].mimeType, value: mediaFiles?.fileTypes[key].mimeType })) : null,
          dataIndex: 'mimeType'
        },
        {
          title: (
            <Row wrap={false} gutter={4} align="middle">
              <Col>
                <Typography.Text ellipsis>
                  {t('File Size')}
                </Typography.Text>
              </Col>
            </Row>
          ),
          dataIndex: 'fileSize',
          responsive: ['xl'],
          key: 'status',
          sorter: true,
          ellipsis: true,
          render: fileSize => {
            return transformFileSize(fileSize);
          }
        },
        {
          title: (
            <Row wrap={false} gutter={4} align="middle">
              <Col>
                <Typography.Text ellipsis>
                  {t('Uploaded at')}
                </Typography.Text>
              </Col>
            </Row>
          ),
          dataIndex: 'createdAt',
          responsive: ['xl'],
          key: 'createdAt',
          width: 150,
          sorter: true,
          ellipsis: true,
          render: (_text, record) => {
            return moment.tz(record.createdAt, organization?.timezone).format(DEFAULT_DATETIME_FORMAT);
          }
        },
        {
          title: t('Uploaded By'),
          dataIndex: 'createdBy',
          key: 'createdBy',
          filters: mediaFiles?.uploadedBy ? Object.keys(mediaFiles?.uploadedBy).map(key => ({ text: mediaFiles?.uploadedBy[key].createdBy, value: mediaFiles?.uploadedBy[key].createdBy })) : null,
          responsive: ['xl'],
          sorter: true,
          ellipsis: true
        },
        {
          title: t('Actions'),
          key: 'actions',
          width: 160,
          align: 'center',
          render: record => {
            return (
              <Space>
                <Button
                  shape="circle"
                  type="text"
                  icon={<EyeOutlined />}
                  loading={isFetchingMediaFiles || isCreatingMediaFile || isDeletingMediaFile}
                  onClick={() => {
                    setFileId(record.id);
                    setIsPreviewModalOpen(true);
                  }}
                />

                <Button
                  shape="circle"
                  type="text"
                  icon={<CopyOutlined />}
                  loading={isFetchingMediaFiles || isCreatingMediaFile || isDeletingMediaFile}
                  onClick={() => copyTextToClipboard(record.url)}
                />

                <Popconfirm
                  title={t('Are you sure you want to delete this file?')}
                  okText={t('Yes')}
                  cancelText={t('No')}
                  placement="topRight"
                  onConfirm={() => handleDelete(record)}
                >
                  <Button
                    shape="circle"
                    type="text"
                    icon={<DeleteIcon />}
                    loading={isFetchingMediaFiles || isCreatingMediaFile || isDeletingMediaFile}
                  />
                </Popconfirm>
              </Space>
            );
          }
        }
      ]}
    />
  );
};

MediaTable.propTypes = {
  page: PropTypes.number,
  selectedFiles: PropTypes.array,
  setSelectedFiles: PropTypes.func,
  handleDelete: PropTypes.func,
  urlSearchParams: PropTypes.func
};

export default MediaTable;
